/**
 * Sonner Toast Component
 *
 * Wrapper around Sonner's Toaster with Slate monochromatic styling.
 * Follows design-system.md: Professional, non-intrusive, slate colors.
 */

import { Toaster as SonnerToaster, toast as sonnerToast } from 'sonner'
import type { ToastOptions } from '../../lib/toast'

/**
 * Custom styled Toaster component
 *
 * Positioned bottom-right to avoid blocking important UI elements.
 * Styled with Slate monochromatic colors matching our design system.
 *
 * @example
 * // In App.tsx or root layout:
 * <Toaster />
 */
export function Toaster() {
  return (
    <SonnerToaster
      position="bottom-right"
      expand={false}
      richColors
      closeButton
      toastOptions={{
        duration: 4000,
        classNames: {
          toast: `
            border border-border
            bg-card
            text-card-foreground
            shadow-lg
            rounded-lg
            font-sans
          `,
          title: 'text-sm font-semibold text-foreground',
          description: 'text-sm text-muted-foreground',
          actionButton: `
            bg-primary
            text-primary-foreground
            hover:bg-primary/90
            rounded-md
            px-3 py-1.5
            text-sm
            font-medium
            transition-colors
          `,
          cancelButton: `
            bg-muted
            text-muted-foreground
            hover:bg-muted/80
            rounded-md
            px-3 py-1.5
            text-sm
            font-medium
            transition-colors
          `,
          closeButton: `
            text-muted-foreground
            hover:text-foreground
            transition-colors
          `,
          error: 'border-l-4 border-l-destructive',
          success: 'border-l-4 border-l-green-600 dark:border-l-green-500',
          warning: 'border-l-4 border-l-amber-600 dark:border-l-amber-500',
          info: 'border-l-4 border-l-blue-600 dark:border-l-blue-500',
        },
      }}
    />
  )
}

/**
 * Toast notification functions
 *
 * Simple, imperative API for showing toasts.
 *
 * @example
 * import { toast } from '@/components/ui/sonner'
 *
 * toast.success('Invoice created successfully')
 * toast.error('Failed to create invoice')
 * toast.warning('Invoice is overdue')
 * toast.info('Processing...')
 */

export const toast = {
  /**
   * Show success toast (green accent)
   */
  success: (message: string, options?: Omit<ToastOptions, 'variant'>) => {
    return sonnerToast.success(message, {
      description: options?.description,
      duration: options?.duration,
      icon: options?.icon,
    })
  },

  /**
   * Show error toast (red accent)
   */
  error: (message: string, options?: Omit<ToastOptions, 'variant'>) => {
    return sonnerToast.error(message, {
      description: options?.description,
      duration: options?.duration,
      icon: options?.icon,
    })
  },

  /**
   * Show warning toast (amber accent)
   */
  warning: (message: string, options?: Omit<ToastOptions, 'variant'>) => {
    return sonnerToast.warning(message, {
      description: options?.description,
      duration: options?.duration,
      icon: options?.icon,
    })
  },

  /**
   * Show info toast (blue accent)
   */
  info: (message: string, options?: Omit<ToastOptions, 'variant'>) => {
    return sonnerToast.info(message, {
      description: options?.description,
      duration: options?.duration,
      icon: options?.icon,
    })
  },

  /**
   * Show toast with custom variant
   */
  show: (message: string, options: ToastOptions) => {
    const variant = options.variant || 'info'

    switch (variant) {
      case 'success':
        return sonnerToast.success(message, {
          description: options.description,
          duration: options.duration,
          icon: options.icon,
        })
      case 'error':
        return sonnerToast.error(message, {
          description: options.description,
          duration: options.duration,
          icon: options.icon,
        })
      case 'warning':
        return sonnerToast.warning(message, {
          description: options.description,
          duration: options.duration,
          icon: options.icon,
        })
      case 'info':
      default:
        return sonnerToast.info(message, {
          description: options.description,
          duration: options.duration,
          icon: options.icon,
        })
    }
  },

  /**
   * Programmatically dismiss all toasts
   */
  dismiss: () => {
    sonnerToast.dismiss()
  },

  /**
   * Programmatically dismiss a specific toast
   */
  dismissById: (id: string | number) => {
    sonnerToast.dismiss(id)
  },
}
